import { useQueries } from '@tanstack/react-query';
import { getDataQuery } from 'api';
import { useEffect } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { loginUser } from 'recoil/loginUser';
import { sisCourseState } from 'recoil/subject/sisCourseState';

export const useGetRequiredApi = (adminSelectState, openYy, openTmGbn, ssCd, openDeptCd) => {
  const user = useRecoilValue(loginUser);

  const checkStudent = user?.role === 'STUDENT';
  const checkTeacher = user?.role === 'TEACHER';
  const checkAdmin = user?.role === 'ADMIN';

  const requiredApis = useQueries({
    queries: [
      {
        //   온라인 학습현황 과목선택 셀렉트박스 쿼리
        queryKey: ['subjectStudent', user],
        queryFn: () => getDataQuery(`/subject/users/me/enrollment/all`),
        enabled: checkStudent,
      },
      {
        //   온라인 학습현황 과목선택 셀렉트박스 쿼리 (학기 선택)
        queryKey: ['subjectTermStudent', user, openYy, openTmGbn],
        queryFn: () => getDataQuery(`/subject/users/me/enrollment/all/term?openYy=${openYy}&openTmGbn=${openTmGbn}`),
        enabled: checkStudent && !!openYy && !!openTmGbn,
      },
      {
        // 교수자 / 관리자 - [학습현황] 등록되어 있는 게시중인 과목 리스트 정보 API
        // subject 상위 select-box list
        queryKey: ['teacherSubjectTitle', user],
        queryFn: () => getDataQuery('/teach/me/courses'),
        enabled: checkTeacher,
      },
      {
        // 교수자 / 관리자 - [학습현황] 등록되어 있는 게시중인 과목 리스트 정보 API (학기 선택)
        // subject 상위 select-box list
        queryKey: ['teacherTermSubjectTitle', user, openYy, openTmGbn],
        queryFn: () => getDataQuery(`/teach/me/courses/term?openYy=${openYy}&openTmGbn=${openTmGbn}`),
        enabled: checkTeacher && !!openYy && !!openTmGbn,
      },
      {
        // 관리자 - 전체 학과 리스트 조회 (학기별 필터링)
        queryKey: ['adminSubjectTitle', user, openYy, openTmGbn],
        queryFn: () => getDataQuery(`/teach/courses/sub-accounts?openYy=${openYy}&openTmGbn=${openTmGbn}`),
        enabled: checkAdmin && !!openYy && !!openTmGbn,
      },
      {
        // 관리자 - 해당 학과의 과목 리스트
        queryKey: ['adminSubjectSubTitle', user, adminSelectState, openYy, openTmGbn],
        queryFn: () => getDataQuery(`/teach/courses/account/${adminSelectState}/courses?openYy=${openYy}&openTmGbn=${openTmGbn}`),
        enabled: checkAdmin && !!adminSelectState && !!openYy && !!openTmGbn,
      },
      {
        // 관리자 - 해당 학과의 담당 교수, 지도 교수 조회
        queryKey: ['adminTeacherInfo', user, openYy, openTmGbn, ssCd, openDeptCd],
        queryFn: () =>
          getDataQuery(
            `/teach/info/admin/courses?openYy=${openYy}&openTmGbn=${openTmGbn}&ssCd=${ssCd}&openDeptCd=${openDeptCd}`
          ),
        enabled: checkAdmin && !!openYy && !!openTmGbn && !!ssCd && !!openDeptCd,
      },
      {
        // 교수자 - 해당 학과의 담당 교수, 지도 교수 조회
        queryKey: ['teacherInfo', user, openYy, openTmGbn, ssCd, openDeptCd],
        queryFn: () => getDataQuery(`/teach/info/courses?openYy=${openYy}&openTmGbn=${openTmGbn}`),
        enabled: checkTeacher && !!openYy && !!openTmGbn,
      },
    ],
  });

  const [
    studentSelectBoxQuery,
    studentTermSelectBoxQuery,
    teacherSelectBoxQuery,
    teacherTermSelectBoxQuery,
    adminSelectBoxQuery,
    adminSubSelectBoxQuery,
    adminTeacherInfoSelectBoxQuery,
    teacherInfoSelectBoxQuery,
  ] = requiredApis.map((query) => query);
  const [
    studentSelectBoxQueryData,
    studentTermSelectBoxQueryData,
    teacherSelectBoxQueryData,
    teacherTermSelectBoxQueryData,
    adminSelectBoxQueryData,
    adminSubSelectBoxQueryData,
    adminTeacherInfoSelectBoxQueryData,
    teacherInfoSelectBoxQueryData,
  ] = requiredApis.map((query) => query?.data?.data);

  const { isLoading: studentSelectBoxQueryIsLoading, isError: studentSelectBoxQueryIsError } = studentSelectBoxQuery;
  const { isLoading: studentTermSelectBoxQueryIsLoading, isError: studentTermSelectBoxQueryIsError } =
    studentTermSelectBoxQuery;
  const { isLoading: teacherSelectBoxQueryIsLoading, isError: teacherSelectBoxQueryIsError } = teacherSelectBoxQuery;
  const { isLoading: teacherTermSelectBoxQueryIsLoading, isError: teacherTermSelectBoxQueryIsError } =
    teacherTermSelectBoxQuery;
  const { isLoading: adminSelectBoxQueryIsLoading, isError: adminSelectBoxQueryIsError } = adminSelectBoxQuery;
  const { isLoading: adminSubSelectBoxQueryIsLoading, isError: adminSubSelectBoxQueryIsError } = adminSubSelectBoxQuery;
  const { isLoading: adminTeacherInfoSelectBoxQueryIsLoading, isError: adminTeacherInfoSelectBoxQueryIsError } =
    adminTeacherInfoSelectBoxQuery;
  const { isLoading: teacherInfoSelectBoxQueryIsLoading, isError: teacherInfoSelectBoxQueryIsError } =
    teacherInfoSelectBoxQuery;

  return {
    //   온라인 학습현황 과목선택 셀렉트박스 쿼리
    studentSelectBoxQueryData,
    studentSelectBoxQueryIsLoading,
    studentSelectBoxQueryIsError,

    //   온라인 학습현황 과목선택 셀렉트박스 쿼리 (학기 선택)
    studentTermSelectBoxQueryData,
    studentTermSelectBoxQueryIsLoading,
    studentTermSelectBoxQueryIsError,

    // 교수자 / 관리자 - [학습현황] 등록되어 있는 게시중인 과목 리스트 정보 API
    // subject 상위 select-box list
    teacherSelectBoxQueryData,
    teacherSelectBoxQueryIsLoading,
    teacherSelectBoxQueryIsError,

    // 교수자 / 관리자 - [학습현황] 등록되어 있는 게시중인 과목 리스트 정보 API (학기 선택)
    // subject 상위 select-box list
    teacherTermSelectBoxQueryData,
    teacherTermSelectBoxQueryIsLoading,
    teacherTermSelectBoxQueryIsError,

    // 관리자 - 전체 학과 리스트 조회
    adminSelectBoxQueryData,
    adminSelectBoxQueryIsLoading,
    adminSelectBoxQueryIsError,

    // 관리자 - 해당 학과의 과목 리스트
    adminSubSelectBoxQueryData,
    adminSubSelectBoxQueryIsLoading,
    adminSubSelectBoxQueryIsError,

    // 관리자 - 해당 학과의 담당 교수, 지도 교수 조회
    adminTeacherInfoSelectBoxQueryData,
    adminTeacherInfoSelectBoxQueryIsLoading,
    adminTeacherInfoSelectBoxQueryIsError,

    // 관리자 - 해당 학과의 담당 교수, 지도 교수 조회
    teacherInfoSelectBoxQueryData,
    teacherInfoSelectBoxQueryIsLoading,
    teacherInfoSelectBoxQueryIsError,
  };
};
